import React, { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import type { InputTableCellParams, InputTableRowParams, RepeatRowsPayload } from '@/components/custom/erp';
import type { PasteResolverConfig, PhieuChiCellValue, PhieuChiDetailRow } from '../../../types';
import { useInvoiceHydration } from '@/hooks/queries/useInvoiceHydration';
import { SelectedCellInfo } from '../hooks/useDetailRows';
import { InputTable } from '@/components/custom/erp';
import { copyCellValue } from '@/lib/table-handlers';
import { useCustomerBalance } from '../../../hooks';
import { getDetailTableColumns } from './columns';
import { useDetailPaste } from '../hooks';
import { FormMode } from '@/types/form';
import ActionBar from './ActionBar';

interface DetailTabProps {
  formMode: FormMode;
  rows: PhieuChiDetailRow[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: (params: InputTableRowParams) => void;
  onCellClick?: (params: InputTableCellParams) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: PhieuChiCellValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<PhieuChiDetailRow>) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (configs: Record<string, PasteResolverConfig>) => void;
  isPasting?: boolean;
}

export const DetailTab: React.FC<DetailTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  onRepeatForRows,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const [ma_ngv, dien_giai, ngayCt] = useWatch({ name: ['ma_ngv', 'dien_giai', 'ngay_ct'] });
  const [displayTax, setDisplayTax] = useState(false);
  const balanceItems = useMemo(
    () =>
      rows.map(row => ({
        ma_kh: row.ma_kh_data?.uuid || '',
        tk: row.tk_no_data?.uuid
      })),
    [rows]
  );
  const { getCustomerBalance } = useCustomerBalance({ ngayCt, items: balanceItems });
  useInvoiceHydration({
    rows,
    enabled: formMode !== 'add',
    onHydrated: (uuid, invoice) => onCellValueChange(uuid, 'id_hd_data', invoice)
  });
  const columns = useMemo(
    () => getDetailTableColumns(ma_ngv, dien_giai, displayTax, onCellValueChange, getCustomerBalance, formMode),
    [ma_ngv, dien_giai, displayTax, onCellValueChange, getCustomerBalance, formMode]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  useEffect(() => {
    setDisplayTax(rows.some(row => Number(row.ma_loai_hd) > 0));
  }, [rows]);
  return (
    <InputTable
      rows={rows}
      autoHeight
      bottomSelector='[data-arito-bottom-bar]'
      onRowClick={onRowClick}
      onCellClick={onCellClick}
      selectedRowId={selectedRowUuid || undefined}
      columns={columns}
      getRowId={row => row?.uuid || ''}
      enableContextMenu
      onCopyData={({ columnValue, columnField, columnValues }) => {
        copyCellValue({
          columnField,
          columnValue,
          columnValues,
          fallback: text => {
            console.log('Copy data per column:', text);
          }
        });
      }}
      onRepeatForRows={onRepeatForRows}
      actionButtons={
        <ActionBar
          formMode={formMode}
          handleAddRow={onAddRow}
          handleDeleteRow={onDeleteRow}
          handleCopyRow={onCopyRow}
          handlePasteRow={onPasteRow}
          selectedCell={selectedCell}
          handleMoveRow={onMoveRow}
          handleExport={() => console.log('Export clicked')}
          handlePin={() => console.log('Pin clicked')}
          isPasting={isPasting}
        />
      }
    />
  );
};
